import { todayStr, toLocalDateStr } from "../lib/date";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { getStudents, getAttendance, getAbsences, getRoster, getClinicalPlansDueForReview, getLicenseAlerts, getDnaTracker, createBatchAssessments, getSchoolDayNumber, getOverdueClinicalReviews, getMyClinicalReviewCount, clearAllClinicalReviews } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import DnaReviewDialog from "../components/assessment/DnaReviewDialog";
import { toast } from "sonner";
import MyTasksWidget from "../components/MyTasksWidget";
import RiskAssessmentReminder from "../components/RiskAssessmentReminder";
import SetupWizardAutoPopup from "../components/SetupWizard";
import GoalsDueSoonCard from "../components/GoalsDueSoonCard";
import BullyingMonitorCard from "../components/BullyingMonitorCard";
import MissingDistrictCard from "../components/MissingDistrictCard";
import ScheduleChangesWeekCard from "../components/ScheduleChangesWeekCard";

export default function DashboardPage() {
  const { user, isAdmin, hasRole } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    presentToday: 0,
    absentToday: 0,
    tardyToday: 0,
    departedToday: 0,
  });
  const [roster, setRoster] = useState(null);
  const [schoolDayInfo, setSchoolDayInfo] = useState(null);
  const [planReviews, setPlanReviews] = useState({ overdue: [], upcoming: [], total_overdue: 0, total_upcoming: 0 });
  const [licenseAlerts, setLicenseAlerts] = useState({ alerts: [], total: 0, expired: 0, critical: 0, warning: 0, upcoming: 0 });
  const [overdueReviews, setOverdueReviews] = useState({ events: [], total: 0, pending_staff_total: 0 });
  const [clinicalReviewCount, setClinicalReviewCount] = useState(0);
  const [showOverdueDetails, setShowOverdueDetails] = useState(false);
  const [dnaCollapsed, setDnaCollapsed] = useState(true);
  const [dnaReviewOpen, setDnaReviewOpen] = useState(false);
  const [clinicianDnaOpen, setClinicianDnaOpen] = useState(false);
  const [clearingReviews, setClearingReviews] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedStat, setExpandedStat] = useState(null);
  const [dnaTracker, setDnaTracker] = useState({ students: [], blocks: [], date: "", total_students_flagged: 0 });
  const [uncheckedStudents, setUncheckedStudents] = useState([]);
  const [uncheckedDate, setUncheckedDate] = useState("");
  const [oneOnOneDialogOpen, setOneOnOneDialogOpen] = useState(false);
  const [oneOnOneStudent, setOneOnOneStudent] = useState(null);
  const [oneOnOneBlock, setOneOnOneBlock] = useState("");
  const [oneOnOneClass, setOneOnOneClass] = useState("1:1 Session");
  const [oneOnOneNotes, setOneOnOneNotes] = useState("");
  const [submittingOneOnOne, setSubmittingOneOnOne] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const today = todayStr();
        // Previous school day (skip weekends) for the "unchecked students" alert
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        while (yesterday.getDay() === 0 || yesterday.getDay() === 6) {
          yesterday.setDate(yesterday.getDate() - 1);
        }
        const prevDate = toLocalDateStr(yesterday);

        // Fetch everything in parallel (secondary calls fail-soft via .catch) to
        // avoid a slow request waterfall on the dashboard.
        const [studentsRes, attendanceRes, rosterRes, planReviewsRes, schoolDayRes,
               licenseRes, dnaRes, prevAttendanceRes, absRes, prevRosterRes, overdueRes, myReviewRes] = await Promise.all([
          getStudents("current"),
          getAttendance({ date: today }),
          getRoster(today),
          getClinicalPlansDueForReview(14),
          getSchoolDayNumber(),
          getLicenseAlerts().catch(() => null),
          getDnaTracker(today).catch(() => null),
          getAttendance({ date: prevDate }).catch(() => null),
          getAbsences(prevDate).catch(() => null),
          getRoster(prevDate).catch(() => null),
          isAdmin() ? getOverdueClinicalReviews().catch(() => null) : Promise.resolve(null),
          getMyClinicalReviewCount().catch(() => null),
        ]);

        const studentsData = studentsRes.data;
        const attendanceData = attendanceRes.data;

        setStudents(studentsData);
        setAttendance(attendanceData);
        setSchoolDayInfo(schoolDayRes.data);

        setStats({
          totalStudents: studentsData.length,
          presentToday: attendanceData.filter((a) => a.status === "present").length,
          absentToday: attendanceData.filter((a) => a.status?.startsWith("absent") || a.status === "excused" || a.status === "unexcused" || a.status === "on_pause").length,
          tardyToday: attendanceData.filter((a) => a.status === "tardy").length,
          departedToday: attendanceData.filter((a) => a.check_out_time).length,
        });

        setRoster(rosterRes.data);
        setPlanReviews(planReviewsRes.data);

        if (licenseRes) setLicenseAlerts(licenseRes.data);
        if (dnaRes) setDnaTracker(dnaRes.data);
        if (overdueRes) setOverdueReviews(overdueRes.data);
        if (myReviewRes) setClinicalReviewCount(myReviewRes.data.count || 0);

        // Unchecked students from the previous school day — only when that day
        // was actually in session (skip weekends, holidays, breaks, out-of-session).
        const prevWasSchoolDay = prevRosterRes?.data?.is_school_day !== false;
        if (prevAttendanceRes && prevWasSchoolDay) {
          const checkedStudentIds = new Set(prevAttendanceRes.data.map(a => a.student_id));
          if (absRes) {
            const finalUnchecked = absRes.data
              .filter(a => a.status === "not_marked" && !checkedStudentIds.has(a.student_id));
            const matchedStudents = finalUnchecked.map(a => studentsData.find(s => s.id === a.student_id)).filter(Boolean);
            if (matchedStudents.length > 0) {
              setUncheckedStudents(matchedStudents);
              setUncheckedDate(prevDate);
            }
          } else {
            const unchecked = studentsData.filter(s => !checkedStudentIds.has(s.id));
            if (unchecked.length > 0) {
              setUncheckedStudents(unchecked);
              setUncheckedDate(prevDate);
            }
          }
        }

      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get students by attendance status
  const getStudentsByStatus = (status) => {
    let studentIds;
    if (status === 'departed') {
      studentIds = attendance.filter((a) => a.check_out_time).map((a) => a.student_id);
    } else {
      studentIds = attendance
        .filter((a) => status === 'absent' 
          ? (a.status?.startsWith("absent") || a.status === "excused" || a.status === "unexcused" || a.status === "on_pause")
          : a.status === status)
        .map((a) => a.student_id);
    }
    return students.filter((s) => studentIds.includes(s.id))
      .sort((a, b) => a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name));
  };

  const toggleExpanded = (stat) => {
    setExpandedStat(expandedStat === stat ? null : stat);
  };

  const handleSubmitOneOnOne = async () => {
    if (!oneOnOneStudent || !oneOnOneBlock) return;
    setSubmittingOneOnOne(true);
    try {
      const today = todayStr();
      await createBatchAssessments({
        date: today,
        group_type: "one_on_one",
        class_name: oneOnOneClass,
        block: oneOnOneBlock,
        teacher_id: user.id,
        assessments: [{
          student_id: oneOnOneStudent.student_id,
          did_not_attend: false,
          participation_level: 3, engagement: 3, communication: 3,
          behavior: 3, attention: 3, work_completion: 3,
          notes: oneOnOneNotes, alternate_plan: "",
        }],
      });
      toast.success(`1:1 assessment saved for ${oneOnOneStudent.student_name}`);
      setOneOnOneDialogOpen(false);
      setOneOnOneNotes("");
      // Refresh DNA tracker
      const dnaRes = await getDnaTracker(today);
      setDnaTracker(dnaRes.data);
    } catch (err) {
      toast.error("Failed to save 1:1 assessment");
    } finally {
      setSubmittingOneOnOne(false);
    }
  };

  const quickActions = [
    {
      title: "Take Attendance",
      description: "Check in students for today",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/>
        </svg>
      ),
      path: "/attendance",
      color: "bg-emerald-500",
    },
    {
      title: "Class Assessment",
      description: "Record student participation",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
      path: "/assessment",
      color: "bg-blue-500",
    },
    {
      title: "Student Files",
      description: "View and manage student records",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      path: "/students",
      color: "bg-violet-500",
    },
    {
      title: "Reports",
      description: "Generate and view reports",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      ),
      path: "/reports",
      color: "bg-amber-500",
    },
  ];

  const today = new Date();
  const dayName = today.toLocaleDateString("en-US", { weekday: "long" });
  const dateStr = today.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const handleClearAllReviews = async () => {
    if (!window.confirm(`Clear the entire clinical plan review backlog? This marks all ${overdueReviews.pending_staff_total} outstanding review(s) across all staff as reviewed. Per-plan review history is kept. This cannot be undone.`)) return;
    setClearingReviews(true);
    try {
      const res = await clearAllClinicalReviews();
      toast.success(`Cleared ${res.data.cleared} outstanding review${res.data.cleared === 1 ? "" : "s"}`);
      setOverdueReviews({ events: [], total: 0, pending_staff_total: 0 });
      setClinicalReviewCount(0);
    } catch {
      toast.error("Failed to clear reviews");
    } finally {
      setClearingReviews(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-[#00305E] tracking-tight">
          Good {today.getHours() < 12 ? "morning" : today.getHours() < 17 ? "afternoon" : "evening"}, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-muted-foreground mt-1">
          {dayName}, {dateStr}
          {roster && !roster.is_school_day && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
              No School{roster.no_school_reason ? ` — ${roster.no_school_reason}` : ""}
            </span>
          )}
          {roster?.is_school_day && roster?.is_half_day && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
              Half Day
            </span>
          )}
        </p>
      </div>

      <GoalsDueSoonCard />

      <ScheduleChangesWeekCard />

      {isAdmin() && <BullyingMonitorCard />}

      {isAdmin() && <MissingDistrictCard />}

      {/* Quick Actions - Moved Up */}
      {/* Unchecked Students Alert */}
      {uncheckedStudents.length > 0 && (
        <Card className="animate-fade-in border-amber-300 bg-amber-50" data-testid="unchecked-students-alert">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center text-white shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">{uncheckedStudents.length} Student{uncheckedStudents.length !== 1 ? "s" : ""} Not Checked In/Out on {new Date(uncheckedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</h3>
                <p className="text-sm text-amber-700 mt-1">These students have no attendance record for the previous school day. Please mark them as excused or unexcused absent.</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {uncheckedStudents.slice(0, 10).map(s => (
                    <span key={s.id} className="px-2 py-1 rounded text-xs bg-amber-100 text-amber-800 font-medium">{s.preferred_name || s.first_name} {s.last_name}</span>
                  ))}
                  {uncheckedStudents.length > 10 && <span className="px-2 py-1 rounded text-xs bg-amber-200 text-amber-800">+{uncheckedStudents.length - 10} more</span>}
                </div>
                <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700" onClick={() => navigate(`/attendance?date=${uncheckedDate}`)} data-testid="resolve-unchecked-btn">
                  Go to Attendance for {new Date(uncheckedDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Tasks & Reminders (from Communications) */}
      <MyTasksWidget />

      {/* Weekly Risk Assessment reminder (Friday onwards, until submitted) */}
      <RiskAssessmentReminder />
      <SetupWizardAutoPopup />

      <div>
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <Card
              key={action.title}
              className={`animate-fade-in stagger-${index + 1} cursor-pointer card-hover group`}
              onClick={() => navigate(action.path)}
              data-testid={`quick-action-${action.title.toLowerCase().replace(/ /g, '-')}`}
            >
              <CardContent className="p-6">
                <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                  {action.icon}
                </div>
                <h3 className="font-semibold text-lg">{action.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{action.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* DNA Tracker Widget - All Staff (before Clinical Plans) */}
      <DnaReviewDialog open={dnaReviewOpen} onOpenChange={setDnaReviewOpen}
        date={dnaTracker.date || todayStr()}
        onSaved={async () => { try { const r = await getDnaTracker(dnaTracker.date || todayStr()); setDnaTracker(r.data); } catch { /* noop */ } }} />
      {dnaTracker.total_students_flagged > 0 && (
        <Card className="animate-fade-in border-2 border-orange-200 bg-orange-50/50" data-testid="dna-tracker-widget">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-600">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Assessment Gaps - Students In School
                </CardTitle>
                <CardDescription>Students present today but missing or DNA'd on assessments. Click to record a 1:1.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-orange-500 text-white text-sm font-semibold" data-testid="dna-tracker-count">
                  {dnaTracker.total_students_flagged} Student{dnaTracker.total_students_flagged !== 1 ? 's' : ''}
                </span>
                <Button size="sm" variant="outline" className="h-8 border-orange-300 text-orange-700 hover:bg-orange-100"
                  onClick={() => setDnaReviewOpen(true)} data-testid="dashboard-review-dna-btn">
                  Review DNA
                </Button>
                <button
                  type="button"
                  onClick={() => setDnaCollapsed((c) => !c)}
                  aria-expanded={!dnaCollapsed}
                  className="p-1.5 rounded-md text-orange-700 hover:bg-orange-100 transition-colors"
                  title={dnaCollapsed ? "Expand" : "Collapse"}
                  data-testid="dna-tracker-toggle">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className={`transition-transform ${dnaCollapsed ? "" : "rotate-180"}`}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>
            </div>
          </CardHeader>
          {!dnaCollapsed && (
          <CardContent>
            <div className="space-y-3">
              {dnaTracker.students.map((student) => (
                <div key={student.student_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white border border-orange-200 hover:border-orange-400 transition-colors"
                  data-testid={`dna-student-${student.student_id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 text-sm font-medium flex-shrink-0">
                      {student.student_name.split(" ").map(n => n.charAt(0)).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{student.student_name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {student.missing_blocks.map(b => (
                          <span key={b} className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium cursor-pointer hover:bg-red-200 transition-colors"
                            data-testid={`dna-gap-${student.student_id}-${b}`}
                            onClick={() => navigate(`/assessment?block=${encodeURIComponent(b)}&date=${dnaTracker.date}&student=${student.student_id}${student.block_classes?.[b] ? `&class_name=${encodeURIComponent(student.block_classes[b])}` : ''}`)}>
                            {b} - No Assessment
                          </span>
                        ))}
                        {student.dna_blocks.map(b => (
                          <span key={b.block} className="px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700 font-medium cursor-pointer hover:bg-orange-200 transition-colors"
                            data-testid={`dna-dna-${student.student_id}-${b.block}`}
                            onClick={() => navigate(`/assessment?block=${encodeURIComponent(b.block)}&date=${dnaTracker.date}&student=${student.student_id}`)}>
                            {b.block} - DNA {b.class_name ? `(${b.class_name})` : ""}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {student.schedule_type === "half_day" ? "Half Day" : "Full Day"} | In: {student.check_in_time || "N/A"}
                        {student.check_out_time ? ` | Out: ${student.check_out_time}` : ""}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-100 flex-shrink-0 ml-2"
                    data-testid={`dna-1on1-btn-${student.student_id}`}
                    onClick={() => {
                      setOneOnOneStudent(student);
                      setOneOnOneBlock(student.missing_blocks[0] || student.dna_blocks[0]?.block || "");
                      setOneOnOneDialogOpen(true);
                    }}>
                    Record 1:1
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {/* Clinician DNA review — quick link with a date picker */}
      {(hasRole("Clinician") || isAdmin()) && (
        <>
          <DnaReviewDialog open={clinicianDnaOpen} onOpenChange={setClinicianDnaOpen}
            date={todayStr()} allowDateChange
            onSaved={async () => { try { const r = await getDnaTracker(dnaTracker.date || todayStr()); setDnaTracker(r.data); } catch { /* noop */ } }} />
          <Card
            className="animate-fade-in border-2 border-orange-200 bg-orange-50/50 cursor-pointer hover:bg-orange-50 transition-colors"
            onClick={() => setClinicianDnaOpen(true)}
            data-testid="dashboard-clinician-dna-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-orange-800">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-600">
                      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    Review DNAs by Date
                  </CardTitle>
                  <CardDescription>Open the DNA review list and pick any date to describe what students did.</CardDescription>
                </div>
                <Button size="sm" variant="outline" className="h-8 border-orange-300 text-orange-700 hover:bg-orange-100"
                  onClick={(e) => { e.stopPropagation(); setClinicianDnaOpen(true); }}
                  data-testid="clinician-review-dna-btn">
                  Review DNAs
                </Button>
              </div>
            </CardHeader>
          </Card>
        </>
      )}
      {clinicalReviewCount > 0 && (
        <Card
          className="animate-fade-in border-2 border-purple-200 bg-purple-50/50 cursor-pointer hover:bg-purple-50 transition-colors"
          onClick={() => navigate("/brp")}
          data-testid="dashboard-clinical-review-rollup"
        >
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold text-gray-800">
                  You have {clinicalReviewCount} clinical plan{clinicalReviewCount === 1 ? "" : "s"} to review
                </p>
                <p className="text-xs text-purple-700">Open the BRP page to scan the updates and mark them all reviewed</p>
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </CardContent>
        </Card>
      )}

      {/* Clinical Plan Review Reminders */}
      {(planReviews.total_overdue > 0 || planReviews.total_upcoming > 0) && (
        <Card className="animate-fade-in border-2 border-orange-200 bg-orange-50/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-600">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Clinical Plan Reviews
                </CardTitle>
                <CardDescription>Plans that need attention</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {planReviews.total_overdue > 0 && (
                  <span className="px-3 py-1 rounded-full bg-red-500 text-white text-sm font-semibold">
                    {planReviews.total_overdue} Overdue
                  </span>
                )}
                {planReviews.total_upcoming > 0 && (
                  <span className="px-3 py-1 rounded-full bg-orange-500 text-white text-sm font-semibold">
                    {planReviews.total_upcoming} Upcoming
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Overdue Reviews */}
              {planReviews.overdue?.map((plan) => (
                <div 
                  key={plan.id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-red-100 border border-red-200 cursor-pointer hover:bg-red-200 transition-colors"
                  onClick={() => navigate(`/students/${plan.student_id}`)}
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                      plan.risk_level === 'high' ? 'bg-red-500 text-white' :
                      plan.risk_level === 'medium' ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'
                    }`}>
                      {plan.risk_level}
                    </span>
                    <div>
                      <p className="font-medium">{plan.student_name}</p>
                      <p className="text-xs text-red-700">{plan.days_overdue} days overdue</p>
                    </div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
              ))}
              
              {/* Upcoming Reviews */}
              {planReviews.upcoming?.map((plan) => (
                <div 
                  key={plan.id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-orange-100 border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors"
                  onClick={() => navigate(`/students/${plan.student_id}`)}
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                      plan.risk_level === 'high' ? 'bg-red-500 text-white' :
                      plan.risk_level === 'medium' ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'
                    }`}>
                      {plan.risk_level}
                    </span>
                    <div>
                      <p className="font-medium">{plan.student_name}</p>
                      <p className="text-xs text-orange-700">
                        Review in {plan.days_until_review} day{plan.days_until_review !== 1 ? 's' : ''} ({new Date(plan.review_date).toLocaleDateString()})
                      </p>
                    </div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* License Expiration Alerts */}
      {licenseAlerts.total > 0 && (
        <Card className={`animate-fade-in border-2 ${licenseAlerts.expired > 0 || licenseAlerts.critical > 0 ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid="license-alerts-widget">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={licenseAlerts.expired > 0 ? "text-red-600" : "text-amber-600"}>
                    <rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>
                  </svg>
                  Staff License Alerts
                </CardTitle>
                <CardDescription>Licenses expiring or requiring renewal</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {licenseAlerts.expired > 0 && (
                  <span className="px-3 py-1 rounded-full bg-red-600 text-white text-sm font-semibold">
                    {licenseAlerts.expired} Expired
                  </span>
                )}
                {licenseAlerts.critical > 0 && (
                  <span className="px-3 py-1 rounded-full bg-red-500 text-white text-sm font-semibold">
                    {licenseAlerts.critical} Critical
                  </span>
                )}
                {licenseAlerts.warning > 0 && (
                  <span className="px-3 py-1 rounded-full bg-amber-500 text-white text-sm font-semibold">
                    {licenseAlerts.warning} Warning
                  </span>
                )}
                {licenseAlerts.upcoming > 0 && (
                  <span className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-semibold">
                    {licenseAlerts.upcoming} Upcoming
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {licenseAlerts.alerts.map((alert, idx) => (
                <div
                  key={`${alert.staff_name}-${alert.license_label}-${alert.expiry_date}`}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                    alert.severity === 'expired' ? 'bg-red-100 border border-red-200 hover:bg-red-200' :
                    alert.severity === 'critical' ? 'bg-red-50 border border-red-200 hover:bg-red-100' :
                    alert.severity === 'warning' ? 'bg-amber-100 border border-amber-200 hover:bg-amber-200' :
                    'bg-blue-50 border border-blue-200 hover:bg-blue-100'
                  }`}
                  onClick={() => navigate('/staff-files')}
                  data-testid={`license-alert-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                      alert.severity === 'expired' ? 'bg-red-600 text-white' :
                      alert.severity === 'critical' ? 'bg-red-500 text-white' :
                      alert.severity === 'warning' ? 'bg-amber-500 text-white' :
                      'bg-blue-500 text-white'
                    }`}>
                      {alert.severity === 'expired' ? 'Expired' :
                       alert.severity === 'critical' ? 'Urgent' :
                       alert.severity === 'warning' ? 'Soon' : 'Notice'}
                    </span>
                    <div>
                      <p className="font-medium">{alert.staff_name}</p>
                      <p className={`text-xs ${
                        alert.severity === 'expired' ? 'text-red-700' :
                        alert.severity === 'critical' ? 'text-red-600' :
                        alert.severity === 'warning' ? 'text-amber-700' : 'text-blue-700'
                      }`}>
                        {alert.license_label}
                        {alert.days_until < 0 
                          ? ` expired ${Math.abs(alert.days_until)} days ago` 
                          : ` expires in ${alert.days_until} days`}
                        {' '}({new Date(alert.expiry_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                      </p>
                    </div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overdue Clinical Plan Reviews (admin) — shown only after the 48h window */}
      {isAdmin() && overdueReviews.total > 0 && (
        <Card className="animate-fade-in border-2 border-red-200 bg-red-50/50" data-testid="overdue-reviews-widget">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/>
                  </svg>
                  Clinical Plan Reviews Overdue
                </CardTitle>
                <CardDescription>Staff who haven't reviewed a clinical plan after 48 hours (email reminders resend every 48h)</CardDescription>
              </div>
              <span className="px-3 py-1 rounded-full bg-red-600 text-white text-sm font-semibold shrink-0">
                {overdueReviews.pending_staff_total} pending
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-red-800" data-testid="overdue-reviews-summary">
                <span className="font-semibold">{overdueReviews.pending_staff_total}</span> outstanding review{overdueReviews.pending_staff_total === 1 ? "" : "s"} across{" "}
                <span className="font-semibold">{overdueReviews.total}</span> plan update{overdueReviews.total === 1 ? "" : "s"}.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowOverdueDetails((v) => !v)}
                  className="text-sm font-medium text-red-700 hover:text-red-900 underline"
                  data-testid="overdue-reviews-toggle"
                >
                  {showOverdueDetails ? "Hide details" : "Show details"}
                </button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  disabled={clearingReviews}
                  onClick={handleClearAllReviews}
                  data-testid="clear-all-reviews-btn"
                >
                  {clearingReviews ? "Clearing…" : "Clear all"}
                </Button>
              </div>
            </div>
            {showOverdueDetails && (
            <div className="space-y-3 mt-3">
              {overdueReviews.events.map((ev) => (
                <div
                  key={ev.event_id}
                  className="p-3 rounded-lg bg-red-100 border border-red-200 cursor-pointer hover:bg-red-200 transition-colors"
                  onClick={() => navigate(`/students/${ev.student_id}?tab=clinical-plans`)}
                  data-testid={`overdue-review-${ev.event_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-medium">
                        {ev.student_name} <span className="text-red-700 font-normal">— plan {ev.event_type === "created" ? "created" : "updated"} by {ev.changed_by_name}</span>
                      </p>
                      <p className="text-xs text-red-700 mt-0.5">
                        {Math.floor((ev.hours_overdue || 0) / 24) >= 1
                          ? `${Math.floor(ev.hours_overdue / 24)} day(s) overdue`
                          : `${ev.hours_overdue}h overdue`}
                        {" · "}{ev.reviewed_count}/{ev.total} reviewed
                      </p>
                      <p className="text-xs text-gray-700 mt-1">
                        <span className="font-semibold">Not yet reviewed:</span> {ev.pending.map((p) => p.name).join(", ")}
                      </p>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 shrink-0 ml-2">
                      <path d="m9 18 6-6-6-6"/>
                    </svg>
                  </div>
                </div>
              ))}
            </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Today's Schedule Info */}
      {roster && (
        <Card className="animate-fade-in cursor-pointer hover:ring-2 hover:ring-[#00305E]/30 transition-all" onClick={() => navigate("/calendar?tab=master-schedule&today=true")} data-testid="today-schedule-widget">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Today's Schedule</CardTitle>
                <CardDescription>
                  {roster.is_school_day ? (
                    roster.is_half_day ? "Half day - Dismissal at 12:00 PM" : "Full day - Dismissal at 2:00 PM"
                  ) : (
                    `No school today${roster.no_school_reason ? ` — ${roster.no_school_reason}` : ""}`
                  )}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" className="shrink-0" onClick={(e) => { e.stopPropagation(); navigate("/calendar?tab=master-schedule&today=true"); }} data-testid="view-master-schedule-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                View Master Schedule
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {roster.events && roster.events.length > 0 && (
              <div className="space-y-2">
                {roster.events.map((event, i) => (
                  <div key={`${event.title}-${event.type}-${i}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className={`w-2 h-2 rounded-full ${
                      event.type === "holiday" ? "bg-red-500" : 
                      event.type === "meeting" ? "bg-blue-500" : "bg-amber-500"
                    }`} />
                    <span className="font-medium">{event.title}</span>
                    {event.is_half_day && (
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">Half Day</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(!roster.events || roster.events.length === 0) && roster.is_school_day && (
              <p className="text-muted-foreground">No special events scheduled for today.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Attendance Summary - Moved Down & Made Interactive */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold">Today's Attendance</h2>
          {schoolDayInfo?.school_day_number && (
            <span className="px-3 py-1 rounded-full bg-[#00305E]/10 text-[#00305E] text-sm font-medium" data-testid="school-day-number">
              Day {schoolDayInfo.school_day_number} of {schoolDayInfo.total_school_days}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {/* Total Students */}
          <Card className="animate-fade-in stagger-1">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Students</p>
                  <p className="text-3xl font-bold mt-1">{stats.totalStudents}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#00305E]/10 flex items-center justify-center text-[#00305E]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Present Today - Clickable */}
          <Card 
            className={`animate-fade-in stagger-2 cursor-pointer transition-all ${expandedStat === 'present' ? 'ring-2 ring-emerald-500' : 'card-hover'}`}
            onClick={() => toggleExpanded('present')}
            data-testid="stat-present"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Present Today</p>
                  <p className="text-3xl font-bold mt-1 text-emerald-600">{stats.presentToday}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click to {expandedStat === 'present' ? 'collapse' : 'view students'}</p>
            </CardContent>
          </Card>

          {/* Absent Today - Clickable */}
          <Card 
            className={`animate-fade-in stagger-3 cursor-pointer transition-all ${expandedStat === 'absent' ? 'ring-2 ring-red-500' : 'card-hover'}`}
            onClick={() => toggleExpanded('absent')}
            data-testid="stat-absent"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Absent Today</p>
                  <p className="text-3xl font-bold mt-1 text-[#A62E1D]">{stats.absentToday}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center text-[#A62E1D]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click to {expandedStat === 'absent' ? 'collapse' : 'view students'}</p>
            </CardContent>
          </Card>

          {/* Tardy Today - Clickable */}
          <Card 
            className={`animate-fade-in stagger-4 cursor-pointer transition-all ${expandedStat === 'tardy' ? 'ring-2 ring-amber-500' : 'card-hover'}`}
            onClick={() => toggleExpanded('tardy')}
            data-testid="stat-tardy"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Tardy Today</p>
                  <p className="text-3xl font-bold mt-1 text-amber-600">{stats.tardyToday}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click to {expandedStat === 'tardy' ? 'collapse' : 'view students'}</p>
            </CardContent>
          </Card>

          {/* Departed Today - Blue */}
          <Card 
            className={`animate-fade-in stagger-5 cursor-pointer transition-all ${expandedStat === 'departed' ? 'ring-2 ring-blue-500' : 'card-hover'}`}
            onClick={() => toggleExpanded('departed')}
            data-testid="stat-departed"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Departed</p>
                  <p className="text-3xl font-bold mt-1 text-blue-600">{stats.departedToday}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click to {expandedStat === 'departed' ? 'collapse' : 'view students'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Expanded Student List */}
        {expandedStat && (
          <Card className="mt-4 animate-fade-in">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${
                  expandedStat === 'present' ? 'bg-emerald-500' :
                  expandedStat === 'absent' ? 'bg-red-500' :
                  expandedStat === 'departed' ? 'bg-blue-500' : 'bg-amber-500'
                }`} />
                {expandedStat === 'present' ? 'Present' : expandedStat === 'absent' ? 'Absent' : expandedStat === 'departed' ? 'Departed' : 'Tardy'} Students
                <span className="text-muted-foreground font-normal">
                  ({getStudentsByStatus(expandedStat).length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getStudentsByStatus(expandedStat).length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  No students marked as {expandedStat} today
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {getStudentsByStatus(expandedStat).map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => navigate(`/students/${student.id}`)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                        expandedStat === 'present' ? 'bg-emerald-500' :
                        expandedStat === 'absent' ? 'bg-red-500' :
                        expandedStat === 'departed' ? 'bg-blue-500' : 'bg-amber-500'
                      }`}>
                        {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {student.preferred_name || student.first_name} {student.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {student.school_district || 'No district'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* 1:1 Assessment Dialog */}
      <Dialog open={oneOnOneDialogOpen} onOpenChange={setOneOnOneDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record 1:1 Assessment</DialogTitle>
          </DialogHeader>
          {oneOnOneStudent && (
            <div className="space-y-4">
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="font-semibold text-[#00305E]">{oneOnOneStudent.student_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Missing: {[...oneOnOneStudent.missing_blocks, ...oneOnOneStudent.dna_blocks.map(d => d.block)].join(", ")}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Block</Label>
                <Select value={oneOnOneBlock} onValueChange={setOneOnOneBlock}>
                  <SelectTrigger data-testid="1on1-block-select"><SelectValue placeholder="Select block" /></SelectTrigger>
                  <SelectContent>
                    {[...oneOnOneStudent.missing_blocks, ...oneOnOneStudent.dna_blocks.map(d => d.block)].map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>What was the student doing?</Label>
                <Select value={oneOnOneClass} onValueChange={setOneOnOneClass}>
                  <SelectTrigger data-testid="1on1-activity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1:1 Session">1:1 Session</SelectItem>
                    <SelectItem value="Clinical Check-in">Clinical Check-in</SelectItem>
                    <SelectItem value="Outing">Outing</SelectItem>
                    <SelectItem value="Cool Down">Cool Down</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input placeholder="Brief description of what occurred..." value={oneOnOneNotes}
                  onChange={e => setOneOnOneNotes(e.target.value)} data-testid="1on1-notes" />
              </div>
              <Button className="w-full bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleSubmitOneOnOne}
                disabled={submittingOneOnOne || !oneOnOneBlock} data-testid="submit-1on1-btn">
                {submittingOneOnOne ? "Saving..." : "Save 1:1 Assessment"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
